import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { TemplaterAPI } from '@saraa/templater/api/TemplaterAPI.js';
import { type GenerationMonthStats, OrganizationGenerationStats } from '@saraa/templater/entities/GenerationStats.js';
import { MONTHS_WINDOW_DEFAULT, monthKey, parseMonthKey } from '@saraa/templater/generationMonths.js';
import { format } from 'date-fns/format';
import { html, nothing, type TemplateResult } from 'lit';
import { emptyState, errorText, sectionLoader } from './ui.js';

/**
 * A month key back as a local `Date`, for formatting only.
 *
 * Built from the parts rather than parsed, so no timezone conversion can happen on the way: the key was
 * derived in UTC, and `new Date('2026-07')` would be read as UTC midnight and shifted back a month for
 * anyone west of it.
 */
const monthDate = (key: string) => {
  const { year, month } = parseMonthKey(key);
  return new Date(year, month - 1, 1);
};

const monthShort = (key: string) => format(monthDate(key), 'MMM');
const monthLong = (key: string) => format(monthDate(key), 'MMMM yyyy');

const plural = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`;

const tile = (label: string, value: number, tone = 'text-base-content') => html`
  <div class="rounded-box border-base-300 bg-base-200/50 flex flex-col gap-1 border p-3">
    <span class="text-base-content/60 text-xs font-semibold tracking-wide uppercase">${label}</span>
    <span class="${tone} text-2xl leading-none font-semibold tabular-nums">${value}</span>
  </div>
`;

/**
 * One column of the graph.
 *
 * The bar's height is its share of the busiest month, and the split inside it is done with flex weights
 * rather than a second percentage — so completed and failed always add up to exactly the bar they are in,
 * whatever rounding would have done to them.
 *
 * A month with no runs still gets a column: dropping it would compress the axis so that a quiet half-year
 * reads as two consecutive months. It shows a flat rule instead of an invisible bar of height zero.
 */
const bar = (month: GenerationMonthStats, max: number) => {
  const total = month.total ?? 0;
  const failed = month.failed ?? 0;
  const done = month.done ?? 0;
  const key = month.month ?? '';

  const readout = total
    ? `${monthLong(key)}: ${plural(total, 'run')}, ${done} completed, ${failed} failed`
    : `${monthLong(key)}: no runs`;

  return html`
    <!-- \`relative\`, so the bars paint above the gridlines positioned behind them. -->
    <div class="relative flex h-full min-w-0 flex-1 flex-col justify-end gap-1" title=${readout}>
      ${total === 0
        ? html`<div class="bg-base-300 h-0.5 w-full rounded-full"></div>`
        : html`<div
            class="flex w-full flex-col justify-end overflow-hidden rounded-t"
            style="height: ${Math.max(2, (total / max) * 100)}%"
          >
            <div class="bg-error w-full" style="flex: ${failed} 0 0"></div>
            <div class="bg-primary w-full" style="flex: ${done} 0 0"></div>
          </div>`}
    </div>
  `;
};

/**
 * Bars against a labelled scale.
 *
 * There was no scale before — twelve bars, month names underneath, and nothing to say
 * whether the tallest was five runs or five hundred. The axis carries the numbers and the
 * gridlines let two months be compared by eye instead of by hovering each one in turn.
 */
const graph = (months: GenerationMonthStats[]): TemplateResult => {
  const max = Math.max(...months.map(month => month.total ?? 0), 1);
  const mid = Math.round(max / 2);

  return html`
    <div class="flex gap-2">
      <div
        class="text-base-content/40 flex h-40 shrink-0 flex-col justify-between text-right text-[10px] tabular-nums"
        aria-hidden="true"
      >
        <span>${max}</span><span>${mid}</span><span>0</span>
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="relative flex h-40 items-end gap-1">
          <div class="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden="true">
            <div class="border-base-300 border-t border-dashed"></div>
            <div class="border-base-300 border-t border-dashed"></div>
            <div class="border-base-300 border-t"></div>
          </div>
          ${months.map(month => bar(month, max))}
        </div>
        <div class="flex gap-1">
          ${months.map(
            month => html`
              <span class="text-base-content/50 min-w-0 flex-1 truncate text-center text-[10px]">
                ${monthShort(month.month ?? '')}
              </span>
            `,
          )}
        </div>
      </div>
    </div>
  `;
};

const table = (months: GenerationMonthStats[]) => html`
  <div class="overflow-x-auto">
    <table class="table-sm table">
      <thead>
        <tr>
          <th>Month</th>
          <th class="text-right">Runs</th>
          <th class="text-right">Completed</th>
          <th class="text-right">Failed</th>
        </tr>
      </thead>
      <tbody>
        ${months.map(
          month => html`
            <tr>
              <td>${monthLong(month.month ?? '')}</td>
              <td class="text-right tabular-nums">${month.total ?? 0}</td>
              <td class="text-right tabular-nums">${month.done ?? 0}</td>
              <td class="${(month.failed ?? 0) > 0 ? 'text-error' : ''} text-right tabular-nums">
                ${month.failed ?? 0}
              </td>
            </tr>
          `,
        )}
      </tbody>
    </table>
  </div>
`;

/**
 * How much generating happens in this organization, and when.
 *
 * Counted across the whole organization rather than per user: this sits on the organization's own detail
 * page, and "how busy is this organization" is not a question about whoever happens to be looking. The
 * endpoint answers it under its own permission check.
 *
 * Read once per organization rather than subscribed. These are aggregated monthly counts — they change at
 * the pace runs finish, and nobody watches this page waiting for a bar to grow.
 */
export const OrganizationGenerationStatsSection = stateful(({ organizationId }: { organizationId: string }) => {
  const statsQuery = useQuery(
    () =>
      TemplaterAPI.organizationStats(
        organizationId,
        MONTHS_WINDOW_DEFAULT,
      )(selectionSet(() => OrganizationGenerationStats, 2)),
    [organizationId],
  );

  const stats = statsQuery.value as OrganizationGenerationStats | undefined;

  if (statsQuery.loading && !stats) {
    return html`<section class="w-full">${sectionLoader()}</section>`;
  }

  if (statsQuery.error && !stats) {
    return html`
      <section class="w-full">
        ${errorText(`Could not load the generation activity: ${statsQuery.error.message}`)}
      </section>
    `;
  }

  const months = (stats?.months ?? []) as GenerationMonthStats[];
  const totalAllTime = stats?.totalAllTime ?? 0;
  const failedAllTime = stats?.failedAllTime ?? 0;

  const currentMonth = monthKey(new Date());
  const thisMonth = months.find(month => month.month === currentMonth)?.total ?? 0;
  const windowTotal = months.reduce((sum, month) => sum + (month.total ?? 0), 0);

  /**
   * Nothing at all is worth saying out loud rather than drawing an empty axis for — and worth saying
   * *why*, since a long-running organization can legitimately show zero here.
   */
  if (totalAllTime === 0) {
    return html`
      <section class="w-full">
        ${emptyState(html`
          <span class="block">No generation runs recorded for this organization yet.</span>
          <span class="mt-1 block text-xs">Runs from before this overview was added are not counted.</span>
        `)}
      </section>
    `;
  }

  return html`
    <section class="flex w-full flex-col gap-4">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        ${tile('This month', thisMonth)} ${tile(`Last ${MONTHS_WINDOW_DEFAULT} months`, windowTotal)}
        ${tile('All time', totalAllTime)}
        ${tile('Failed', failedAllTime, failedAllTime > 0 ? 'text-error' : 'text-base-content')}
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-base-content/60 flex items-center justify-between text-xs">
          <span>Runs per month</span>
          <span class="flex items-center gap-3">
            <span class="flex items-center gap-1">
              <span class="bg-primary inline-block size-2 rounded-sm"></span> completed
            </span>
            <span class="flex items-center gap-1">
              <span class="bg-error inline-block size-2 rounded-sm"></span> failed
            </span>
          </span>
        </div>
        ${months.length ? graph(months) : nothing}
      </div>

      <!--
        The table is the same twelve numbers the graph already draws. It stays, because a
        number you can read off beats a bar you have to hover — but it opens on request
        rather than doubling the height of the section by default.
      -->
      ${months.length
        ? html`<details class="group">
            <summary
              class="text-base-content/70 hover:text-base-content flex cursor-pointer list-none items-center gap-1 text-xs font-medium marker:content-none"
            >
              <span
                class="icon-[material-symbols--chevron-right] text-base transition-transform group-open:rotate-90"
              ></span>
              Monthly breakdown
            </summary>
            <div class="pt-2">${table([...months].reverse())}</div>
          </details>`
        : nothing}
    </section>
  `;
});
